import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import LandingLayout from "./LandingLayout";
import ParticlesBackground from "../layout/ParticlesBackground";
import { MdTimeline, MdVerifiedUser, MdLightbulb, MdSecurity, MdArrowDownward, MdCheckCircle } from "react-icons/md";

// Import Client Images
import imgOxiquim from "../../img/img_clientes/Oxiquim.png";
import imgBgnews from "../../img/img_clientes/bgnews.png";
import imgCabal from "../../img/img_clientes/cabal.png";
import imgCopec from "../../img/img_clientes/copec.png";
import imgEnap from "../../img/img_clientes/enap.png";
import imgEnex from "../../img/img_clientes/enex.png";
import imgJlc from "../../img/img_clientes/jlc.png";

const ScrollToHash = () => {
    const { hash } = useLocation();
    useEffect(() => {
        if (hash) {
            const element = document.getElementById(hash.replace('#', ''));
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        } else {
            window.scrollTo(0, 0);
        }
    }, [hash]);
    return null;
};

const SectionHeading = ({ children, light = false }) => (
    <h2 className={`text-3xl md:text-5xl font-bold mb-8 tracking-tight ${light ? 'text-white' : 'text-gray-900'}`}>
        {children}
    </h2>
);

const TextBlock = ({ children, light = false }) => (
    <p className={`text-lg md:text-xl leading-relaxed max-w-4xl mx-auto ${light ? 'text-gray-400' : 'text-gray-600'}`}>
        {children}
    </p>
);

const LandingHome = () => {
    // Client Carousel Logic
    const rawClients = [
        { name: "OXIQUIM", logo: imgOxiquim },
        { name: "BG NEWS", logo: imgBgnews },
        { name: "CABAL", logo: imgCabal },
        { name: "COPEC", logo: imgCopec },
        { name: "ENAP", logo: imgEnap },
        { name: "ENEX", logo: imgEnex },
        { name: "JLC", logo: imgJlc },
    ];

    const clients = [...rawClients, ...rawClients, ...rawClients, ...rawClients, ...rawClients];
    const [position, setPosition] = useState(0);
    const itemSpacing = 240;
    const oneSetWidth = rawClients.length * itemSpacing;

    useEffect(() => {
        const intervalId = setInterval(() => {
            setPosition((prev) => {
                const next = prev + 1.5;
                return next >= oneSetWidth ? next - oneSetWidth : next;
            });
        }, 16);
        return () => clearInterval(intervalId);
    }, [oneSetWidth]);


    return (
        <LandingLayout noPt>
            <ScrollToHash />

            {/* --- HERO SECTION --- */}
            <section className="relative h-screen flex flex-col items-center justify-center bg-gray-900 overflow-hidden">
                <ParticlesBackground />

                <div className="relative z-10 px-4 text-center">
                    <div className="w-24 h-1 bg-amber-500 mx-auto mb-12 rounded-full shadow-[0_0_15px_rgba(245,158,11,0.5)]"></div>
                    <h1 className="text-7xl md:text-9xl font-black text-white tracking-tighter mb-8 drop-shadow-2xl">
                        ATECMA
                    </h1>
                    <p className="text-2xl md:text-4xl text-gray-400 font-light tracking-widest uppercase">
                        Excelencia en Hidrocarburos
                    </p>
                </div>

                <div className="absolute bottom-16 animate-bounce text-gray-600">
                    <MdArrowDownward size={32} />
                </div>
            </section>

            {/* --- QUIENES SOMOS (Clean, Large Text) --- */}
            <section id="quienes-somos" className="py-32 bg-white">
                <div className="max-w-5xl mx-auto px-4 text-center">
                    <span className="text-amber-600 font-bold tracking-widest uppercase text-sm mb-4 block">Nuestra Identidad</span>
                    <SectionHeading>Más que servicios, entregamos <span className="text-amber-600">certezas</span>.</SectionHeading>
                    <div className="space-y-8 mt-12">
                        <TextBlock>
                            Desde 2004, ATECMA se ha consolidado como un referente indiscutible en la industria de combustibles.
                            Nuestra trayectoria de más de 20 años es testimonio de un compromiso inquebrantable con la calidad y la independencia técnica.
                        </TextBlock>
                        <TextBlock>
                            Somos especialistas en <strong>Asesorías Técnicas y Mantención</strong>.
                            Las principales compañías distribuidoras del país confían en nosotros para garantizar la precisión de sus mediciones y la continuidad operativa de sus activos críticos.
                        </TextBlock>
                    </div>
                </div>
            </section>

            {/* --- MISION & VISION (Rounded Cards) --- */}
            <section className="py-24 bg-gray-50 border-t border-gray-100">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid md:grid-cols-2 gap-12">
                        {/* Mision Card */}
                        <div className="bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 p-10 border border-gray-100 group">
                            <div className="w-20 h-20 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-600 mb-8 border border-amber-100 group-hover:scale-110 transition-transform duration-300">
                                <MdTimeline size={40} />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-6 group-hover:text-amber-600 transition-colors">Nuestra Misión</h3>
                            <p className="text-gray-600 text-lg leading-relaxed text-justify">
                                Proveer soluciones integrales de mantención y calibración que aseguren la exactitud operativa y el cumplimiento normativo, protegiendo los activos y la reputación de nuestros clientes con estándares de clase mundial.
                            </p>
                        </div>

                        {/* Vision Card */}
                        <div className="bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 p-10 border border-gray-100 group">
                            <div className="w-20 h-20 bg-gray-900 rounded-2xl flex items-center justify-center text-amber-500 mb-8 border border-gray-800 group-hover:scale-110 transition-transform duration-300">
                                <MdVerifiedUser size={40} />
                            </div>
                            <h3 className="text-3xl font-bold text-gray-900 mb-6 group-hover:text-amber-600 transition-colors">Nuestra Visión</h3>
                            <p className="text-gray-600 text-lg leading-relaxed text-justify">
                                Ser reconocidos a nivel nacional como el socio estratégico más confiable en la industria de hidrocarburos, liderando la transformación tecnológica y elevando los estándares de seguridad y calidad en cada operación.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- CLIENTS CAROUSEL SECTION (Copied from Experiencia) --- */}
            <section className="py-24 bg-white border-y border-gray-100 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 mb-16 text-center">
                    <span className="text-amber-600 font-bold tracking-widest uppercase text-sm mb-4 block">Confianza Corporativa</span>
                    <SectionHeading>Respaldados por los <span className="text-amber-600">Líderes</span> de la Industria</SectionHeading>
                </div>

                <div className="relative w-full h-[300px] flex items-center justify-center perspective-1000 bg-gradient-to-t from-gray-50 to-white">
                    <div className="absolute w-full h-full flex items-center justify-center overflow-hidden">
                        {/* Gradient Overlays */}
                        <div className="hidden md:block absolute left-0 top-0 w-32 h-full bg-gradient-to-r from-white via-white/80 to-transparent z-20 pointer-events-none" />
                        <div className="hidden md:block absolute right-0 top-0 w-32 h-full bg-gradient-to-l from-white via-white/80 to-transparent z-20 pointer-events-none" />

                        <div className="relative w-full h-full flex items-center justify-center">
                            {clients.map((client, index) => {
                                const initialOffset = (clients.length * itemSpacing) / 2;
                                let realX = (index * itemSpacing) - position - initialOffset;
                                const distanceFromCenter = Math.abs(realX);
                                const maxDist = 800; // range of effect
                                const scaleMin = 0.6;
                                const scaleMax = 1.1;
                                const centerFactor = 1 - Math.min(distanceFromCenter, maxDist) / maxDist;
                                const scale = scaleMin + (scaleMax - scaleMin) * centerFactor;
                                const maxRot = 50;
                                const rotationY = (realX / maxDist) * -maxRot;
                                if (distanceFromCenter > 1200) return null;
                                const opacity = 0.6 + 0.4 * centerFactor;

                                return (
                                    <div
                                        key={`${client.name}-${index}`}
                                        className="absolute top-1/2 left-1/2 flex flex-col items-center justify-center p-4 transition-transform duration-75 will-change-transform backface-visibility-visible"
                                        style={{
                                            transform: `translate(-50%, -50%) translate3d(${realX}px, 0, 0) scale(${scale}) rotateY(${rotationY}deg)`,
                                            zIndex: 100 - Math.round(distanceFromCenter / 10),
                                            opacity: opacity,
                                            width: '200px'
                                        }}
                                    >
                                        <div className="w-32 h-32 md:w-36 md:h-36 bg-white rounded-xl shadow-lg flex items-center justify-center p-4 border-2 border-gray-200 hover:border-amber-400 transition-colors duration-300">
                                            <img
                                                src={client.logo}
                                                alt={client.name}
                                                className="max-w-full max-h-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300 transform hover:scale-110"
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* --- VALORES (Dark & Impactful) --- */}
            <section className="py-32 bg-gray-900 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-gray-800 via-gray-900 to-black opacity-50"></div>

                <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
                    <span className="text-amber-500 font-bold tracking-widest uppercase text-sm mb-4 block">Nuestros Pilares</span>
                    <SectionHeading light>Valores Corporativos</SectionHeading>

                    <div className="grid md:grid-cols-3 gap-12 mt-20">
                        <div className="p-8 border border-gray-800 rounded-3xl hover:bg-gray-800 transition-colors hover:border-amber-500/30">
                            <MdSecurity className="text-amber-500 w-12 h-12 mx-auto mb-6" />
                            <h4 className="text-xl font-bold mb-4">Seguridad Intransable</h4>
                            <p className="text-gray-400">Nuestro compromiso con la vida y el medio ambiente no es negociable. Operamos bajo los más estrictos estándares.</p>
                        </div>
                        <div className="p-8 border border-gray-800 rounded-3xl hover:bg-gray-800 transition-colors hover:border-amber-500/30">
                            <MdVerifiedUser className="text-amber-500 w-12 h-12 mx-auto mb-6" />
                            <h4 className="text-xl font-bold mb-4">Integridad</h4>
                            <p className="text-gray-400">La transparencia y la ética guían cada informe, cada medición y cada relación comercial que establecemos.</p>
                        </div>
                        <div className="p-8 border border-gray-800 rounded-3xl hover:bg-gray-800 transition-colors hover:border-amber-500/30">
                            <MdLightbulb className="text-amber-500 w-12 h-12 mx-auto mb-6" />
                            <h4 className="text-xl font-bold mb-4">Innovación</h4>
                            <p className="text-gray-400">Buscamos constantemente nuevas tecnologías y metodologías para optimizar los procesos de nuestros clientes.</p>
                        </div>
                    </div>
                </div>
            </section>

        </LandingLayout>
    );
};

export default LandingHome;
